import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ClearOutlined,
  CreditCardOutlined,
  DownloadOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  MailOutlined,
  ReloadOutlined,
  SearchOutlined,
  SendOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { useSnackbar } from "notistack";
import API from "../services/api";
import RowActions from "../components/RowActions";
import PaymentDetailsDrawer from "../components/PaymentDetailsDrawer";
import { useAuth } from "../context/AuthContext";
import { canWrite } from "../constants/permissions";
import {
  PAYMENT_LINK_STATUS_OPTIONS,
  PaymentStatuses,
  customerInitials,
  describeEmailTrouble,
  describeLinkTimestamp,
  describePaymentLinkStatus,
  describePaymentStatus,
  describeTaxBreakdown,
  formatSnapshotAmount,
  resolvePaymentActions,
} from "../utils/payments";
import {
  cancelPaymentLink,
  describeApiError,
  downloadInvoice,
  fetchPayment,
  fetchPaymentSummary,
  fetchPayments,
  resendPaymentLink,
  resendReceipt,
} from "../services/paymentsApi";

const { RangePicker } = DatePicker;

// One sort control rather than per-column header arrows. This table reads as a
// list of records rather than a spreadsheet, and the two mechanisms would
// contradict each other the moment a reader used both.
const SORT_OPTIONS = [
  { value: "createdAt:desc", label: "Newest first" },
  { value: "createdAt:asc", label: "Oldest first" },
  { value: "captured_at:desc", label: "Recently paid" },
  { value: "amount:desc", label: "Highest amount" },
  { value: "amount:asc", label: "Lowest amount" },
];

// The order the status tabs appear in: what was collected, what is still owed,
// then what needs a human. A status missing from this list still gets a tab
// when rows exist in it -- see `statusTabs` below.
const STATUS_TAB_ORDER = [
  PaymentStatuses.CAPTURED,
  PaymentStatuses.PENDING,
  PaymentStatuses.FAILED,
  PaymentStatuses.RECONCILIATION_REQUIRED,
  PaymentStatuses.REFUNDED,
];

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
};

// Sensitive operations confirm first. Resending a payment link mails a
// customer and cancelling one takes away a URL they may be about to use, so
// neither should happen on a single misplaced click in an overflow menu.
const CONFIRMATIONS = {
  resendLink: {
    title: "Resend payment link?",
    body: "The customer will receive the same payment link again. No new link is created.",
    okText: "Resend link",
    danger: false,
  },
  cancelLink: {
    title: "Cancel payment link?",
    body:
      "The link stops working immediately and the customer can no longer pay with it. This cannot be undone — a new link would have to be generated.",
    okText: "Cancel link",
    danger: true,
  },
  resendReceipt: {
    title: "Resend receipt?",
    body:
      "The customer will receive the payment receipt again. This does not change the payment or renew the subscription.",
    okText: "Resend receipt",
    danger: false,
  },
};

/**
 * One figure in the row of cards above the payment table
 *
 * Takes already-rendered lines rather than an amount, because the summary is
 * grouped by currency: a filtered set spanning two currencies gets a line each
 * instead of one number that added them together.
 */
const SummaryStat = ({ title, hint, loading, lines, fallback }) => (
  <Card
    className="app-page-card payment-stat"
    variant="borderless"
    styles={{ body: { padding: 20 } }}
  >
    <Tooltip title={hint} placement="bottomLeft">
      <span className="payment-stat__label">{title}</span>
    </Tooltip>
    {loading ? (
      <Skeleton.Input active size="small" style={{ width: 150, marginTop: 10 }} />
    ) : (
      <div className="payment-stat__values">
        {lines.length ? (
          lines.map((line) => (
            <div key={line.key} className="payment-stat__value">
              {line.value}
            </div>
          ))
        ) : (
          <div className="payment-stat__value payment-stat__value--empty">{fallback}</div>
        )}
      </div>
    )}
  </Card>
);

const PaymentHistory = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const mayManagePayments = canWrite(user, "payments");

  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [actionInFlight, setActionInFlight] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  const [linkStatusFilter, setLinkStatusFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [sortValue, setSortValue] = useState(SORT_OPTIONS[0].value);

  const [detailPayment, setDetailPayment] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Changing a filter costs two requests -- one from the filter itself, one
  // from the page reset that follows it -- and switching status tab does the
  // same. Whichever is answered last is not necessarily the one asked last, so
  // each request carries a sequence number and a response that is no longer
  // the newest is dropped rather than rendered over the newer one.
  const listRequestRef = useRef(0);
  const summaryRequestRef = useRef(0);

  const [from, to] = dateRange || [];
  const fromParam = from ? from.startOf("day").toISOString() : "";
  const toParam = to ? to.endOf("day").toISOString() : "";

  // Everything that narrows the set, shared by the list and the summary so the
  // figures on the cards always describe the rows underneath them.
  //
  // The status filter is deliberately not in here. The summary ignores it, and
  // has to: the tabs that set it are built from its own counts, and a tab row
  // whose numbers moved every time a tab was clicked could not be used to
  // navigate.
  const scopeParams = useMemo(
    () => ({
      search: debouncedSearchTerm,
      payment_link_status: linkStatusFilter,
      customer_id: customerFilter,
      from: fromParam,
      to: toParam,
    }),
    [debouncedSearchTerm, linkStatusFilter, customerFilter, fromParam, toParam]
  );

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await API.get("/customers");
      setCustomers(response.data.data || []);
    } catch (error) {
      // A missing customer lookup only costs the filter dropdown; the payment
      // list itself carries the customer name it needs.
      setCustomers([]);
    }
  }, []);

  const loadPayments = useCallback(async () => {
    setLoading(true);
    const [sortBy, sortOrder] = sortValue.split(":");
    const sequence = ++listRequestRef.current;

    try {
      const response = await fetchPayments({
        ...scopeParams,
        page: page + 1,
        limit: rowsPerPage,
        payment_status: paymentStatusFilter,
        sortBy,
        sortOrder,
      });

      if (sequence !== listRequestRef.current) return;

      setPayments(response.data.data || []);
      setTotalCount(
        typeof response.data.meta?.total === "number"
          ? response.data.meta.total
          : (response.data.data || []).length
      );
    } catch (error) {
      if (sequence !== listRequestRef.current) return;

      enqueueSnackbar(describeApiError(error, "Error fetching payments").message, {
        variant: "error",
      });
    } finally {
      // The spinner clears only on the newest request, or an overtaken one
      // would stop it while the current fetch is still running.
      if (sequence === listRequestRef.current) setLoading(false);
    }
  }, [scopeParams, page, rowsPerPage, paymentStatusFilter, sortValue, enqueueSnackbar]);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    const sequence = ++summaryRequestRef.current;

    try {
      const response = await fetchPaymentSummary(scopeParams);

      if (sequence !== summaryRequestRef.current) return;

      setSummary(response.data.data || null);
    } catch (error) {
      if (sequence !== summaryRequestRef.current) return;

      // The cards fall back to an em dash rather than keeping the last set of
      // figures on screen. A total that silently describes a different filter
      // than the table below it would still be believed.
      setSummary(null);
      enqueueSnackbar(describeApiError(error, "Could not load the payment totals").message, {
        variant: "warning",
      });
    } finally {
      if (sequence === summaryRequestRef.current) setSummaryLoading(false);
    }
  }, [scopeParams, enqueueSnackbar]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  // Separate from the list on purpose: paging, sorting and switching status tab
  // do not change what is in scope, so none of them should cost a second
  // aggregate query.
  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // Any change that narrows or reorders the list goes back to page 1, or a
  // smaller result set can leave the table on a page that no longer exists.
  useEffect(() => {
    setPage(0);
  }, [scopeParams, paymentStatusFilter, sortValue]);

  const refreshAll = useCallback(() => {
    loadPayments();
    loadSummary();
  }, [loadPayments, loadSummary]);

  const openDetails = async (payment) => {
    // Opened from the row immediately, then refreshed from the detail endpoint,
    // which returns the full record rather than the list projection.
    setDetailPayment(payment);
    setDetailOpen(true);

    try {
      const response = await fetchPayment(payment.id);
      setDetailPayment(response.data.data);
    } catch (error) {
      enqueueSnackbar(describeApiError(error, "Could not load the full payment record").message, {
        variant: "warning",
      });
    }
  };

  const runAction = async (kind, payment) => {
    const handlers = {
      resendLink: { call: resendPaymentLink, success: "Payment link resent to the customer" },
      cancelLink: { call: cancelPaymentLink, success: "Payment link cancelled" },
      resendReceipt: { call: resendReceipt, success: "Receipt resent to the customer" },
    };

    setActionInFlight(`${kind}:${payment.id}`);
    try {
      await handlers[kind].call(payment.id);
      enqueueSnackbar(handlers[kind].success, { variant: "success" });
    } catch (error) {
      const described = describeApiError(error, "The action could not be completed");
      enqueueSnackbar(described.message, { variant: "error" });
    } finally {
      setActionInFlight(null);
      setConfirmation(null);
      // Refreshed either way: a failed action can still have changed state, as
      // when a cancellation loses the race against a capture. The totals move
      // with the rows, so both are reloaded.
      refreshAll();
      if (detailOpen && detailPayment?.id === payment.id) {
        await fetchPayment(payment.id)
          .then((response) => setDetailPayment(response.data.data))
          .catch(() => {});
      }
    }
  };

  // A read, not a write: downloaded straight from the click, with no
  // confirmation step and no effect on the payment's own state, unlike the
  // actions `runAction` drives.
  const handleDownloadInvoice = async (payment) => {
    setActionInFlight(`downloadInvoice:${payment.id}`);
    try {
      const response = await downloadInvoice(payment.id);
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `Invoice-${payment.reference_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      enqueueSnackbar(describeApiError(error, "Could not download the invoice").message, {
        variant: "error",
      });
    } finally {
      setActionInFlight(null);
    }
  };

  const hasActiveFilters = Boolean(
    searchTerm || paymentStatusFilter || linkStatusFilter || customerFilter || dateRange
  );

  const clearFilters = () => {
    setSearchTerm("");
    setPaymentStatusFilter("");
    setLinkStatusFilter("");
    setCustomerFilter("");
    setDateRange(null);
  };

  const customerOptions = useMemo(
    () => customers.map((customer) => ({ value: customer.id, label: customer.name })),
    [customers]
  );

  const amountLines = (key) =>
    (summary?.totals || []).map((entry) => ({
      key: entry.currency,
      value: entry[`${key}_display`],
    }));

  // A tab for "All", then one per status that actually has rows. A status with
  // nothing in it is not worth a control -- except the one currently selected,
  // which stays so the active tab cannot vanish from under the reader.
  const statusTabs = useMemo(() => {
    const counts = summary?.status_counts || {};
    const ordered = [
      ...STATUS_TAB_ORDER,
      // A status this build has no constant for still gets a tab, rather than
      // leaving rows the reader can see but cannot filter down to.
      ...Object.keys(counts).filter((status) => !STATUS_TAB_ORDER.includes(status)),
    ];

    return [
      { value: "", label: "All", count: summary ? summary.total : null },
      ...ordered
        .filter((status) => counts[status] > 0 || status === paymentStatusFilter)
        .map((status) => ({
          value: status,
          label: describePaymentStatus(status).label,
          count: counts[status] || 0,
        })),
    ];
  }, [summary, paymentStatusFilter]);

  const columns = [
    // Every column is given an explicit width. Without one, the sub-lines
    // under Total and Link -- which must not wrap, since an amount broken
    // across two lines is unreadable -- claim as much width as they need and
    // the customer name is squeezed until it wraps instead, which is what made
    // the rows twice as tall as they should be.
    {
      title: "Customer",
      key: "customer",
      width: 240,
      render: (_, payment) => {
        const name = payment.customer?.name || payment.billing_snapshot?.customer_name || "—";

        return (
          <div className="payment-customer">
            <span className="payment-avatar" aria-hidden="true">
              {customerInitials(name)}
            </span>
            <Tooltip title={name}>
              <span className="payment-customer__name">{name}</span>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Plan",
      key: "plan",
      width: 120,
      render: (_, payment) => payment.plan?.name || payment.billing_snapshot?.plan_name || "—",
    },
    {
      title: "Reference",
      dataIndex: "reference_id",
      key: "reference_id",
      width: 230,
      // Truncated in CSS rather than through antd's `ellipsis`, which fights
      // with `copyable` over the same inline box. The full reference is on the
      // tooltip and on the clipboard, so nothing is lost by shortening it --
      // it is an opaque identifier, read by its ends when read at all.
      render: (value) => (
        <Tooltip title={value}>
          <Typography.Text className="payment-reference" copyable={{ text: value }}>
            <span className="payment-reference__value">{value}</span>
          </Typography.Text>
        </Tooltip>
      ),
    },
    {
      // The breakdown moved under the total rather than into its own two
      // columns. Base and tax are how the total was arrived at, not separate
      // figures anyone scans down a column.
      title: "Total",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      width: 175,
      render: (_, payment) => {
        const breakdown = describeTaxBreakdown(payment);

        return (
          <div className="payment-amount">
            <div className="payment-amount__total">{formatSnapshotAmount(payment, "amount")}</div>
            {breakdown && <div className="payment-cell__sub">{breakdown}</div>}
          </div>
        );
      },
    },
    {
      title: "Payment",
      dataIndex: "payment_status",
      key: "payment_status",
      width: 175,
      render: (value, payment) => {
        const meta = describePaymentStatus(value);
        // An email failure is shown beside the payment status, never folded
        // into it: "we could not tell the customer" and "the customer did not
        // pay" are different problems with different fixes. It appears only
        // when something actually failed, so a healthy row stays quiet.
        const trouble = describeEmailTrouble(payment);

        return (
          <div className="payment-cell">
            <Tag color={meta.color} bordered={false}>
              {meta.label}
            </Tag>
            {trouble && (
              <Tooltip title={trouble.detail}>
                <span className="payment-cell__warning">
                  <ExclamationCircleFilled /> {trouble.label}
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Link",
      dataIndex: "payment_link_status",
      key: "payment_link_status",
      width: 170,
      render: (value, payment) => {
        const meta = describePaymentLinkStatus(value);
        const stamp = describeLinkTimestamp(payment);

        return (
          <div className="payment-cell">
            <Tag color={meta.color} bordered={false}>
              {meta.label}
            </Tag>
            {stamp && (
              <div className="payment-cell__sub">
                {stamp.label} · {stamp.value}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      width: 90,
      fixed: "right",
      render: (_, payment) => {
        const actions = resolvePaymentActions(payment, { canWrite: mayManagePayments });
        const busy = Boolean(actionInFlight);

        // Invalid actions are shown disabled with the reason, rather than
        // hidden: "why can I not resend this?" is a question the reader should
        // be able to answer without guessing.
        const item = (key, label, icon, danger = false) => ({
          key,
          label,
          // The reason an action is unavailable belongs in a tooltip; in the
          // label it stretches the dropdown to the width of the sentence.
          tooltip: actions[key].allowed ? null : actions[key].reason,
          icon,
          danger,
          disabled: !actions[key].allowed || busy,
          onClick: () => setConfirmation({ kind: key, payment }),
        });

        return (
          <RowActions
            disabled={loading}
            items={[
              {
                key: "view",
                label: "View details",
                icon: <EyeOutlined />,
                onClick: () => openDetails(payment),
              },
              item("resendLink", "Resend payment link", <SendOutlined />),
              item("resendReceipt", "Resend receipt", <MailOutlined />),
              {
                key: "downloadInvoice",
                label: "Download invoice",
                icon: <DownloadOutlined />,
                tooltip: actions.downloadInvoice.allowed ? null : actions.downloadInvoice.reason,
                disabled: !actions.downloadInvoice.allowed || busy,
                onClick: () => handleDownloadInvoice(payment),
              },
              item("cancelLink", "Cancel payment link", <StopOutlined />, true),
            ]}
          />
        );
      },
    },
  ];

  const emptyState = (
    <div style={{ padding: 48, textAlign: "center" }}>
      <CreditCardOutlined style={{ fontSize: 56, color: "rgba(100, 116, 139, 0.45)" }} />
      <Typography.Title level={4} style={{ marginTop: 16 }}>
        No payments found
      </Typography.Title>
      <Typography.Text type="secondary">
        {hasActiveFilters
          ? "Clear the filters to see all payments."
          : "Generate a payment link from Subscription Management to take the first payment."}
      </Typography.Text>
      {hasActiveFilters && (
        <div style={{ marginTop: 20 }}>
          <Button icon={<ClearOutlined />} onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      )}
    </div>
  );

  const activeConfirmation = confirmation ? CONFIRMATIONS[confirmation.kind] : null;

  return (
    <div className="payments-page">
      <div className="page-heading">
        <div>
          <Typography.Title level={4} style={{ marginBottom: 4 }}>
            Payment History
          </Typography.Title>
          <Typography.Text type="secondary">
            Subscription payments taken through Razorpay payment links.
          </Typography.Text>
        </div>
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={refreshAll}
          loading={loading}
          aria-label="Refresh payments"
        />
      </div>

      <Row gutter={[16, 16]} className="payment-stats">
        <Col xs={24} sm={12} xl={8}>
          <SummaryStat
            title="Collected"
            hint="Payments Razorpay has confirmed, across everything matching the filters below. Refunds are not subtracted; they are counted under their own status."
            loading={summaryLoading}
            lines={amountLines("collected")}
            fallback="—"
          />
        </Col>
        <Col xs={24} sm={12} xl={8}>
          <SummaryStat
            title="Outstanding"
            hint="Billed and not paid: awaiting payment plus failed. Refunded payments and payments awaiting reconciliation are in neither card, because neither figure would be true of them."
            loading={summaryLoading}
            lines={amountLines("outstanding")}
            fallback="—"
          />
        </Col>
        <Col xs={24} sm={24} xl={8}>
          <SummaryStat
            title="Payments"
            hint="How many payments match the filters below, not how many are on this page."
            loading={summaryLoading}
            lines={summary ? [{ key: "count", value: summary.total }] : []}
            fallback="—"
          />
        </Col>
      </Row>

      <Card className="app-page-card" variant="borderless" styles={{ body: { padding: 20 } }}>
        <div className="payment-filters">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search reference, customer or plan..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="payment-filters__search"
            disabled={loading}
          />
          <Select
            value={customerFilter}
            onChange={setCustomerFilter}
            className="payment-filters__select"
            placeholder="Customer"
            showSearch
            optionFilterProp="label"
            options={[{ value: "", label: "All customers" }, ...customerOptions]}
          />
          <Select
            value={linkStatusFilter}
            onChange={setLinkStatusFilter}
            className="payment-filters__select"
            placeholder="Link status"
            options={[{ value: "", label: "All link statuses" }, ...PAYMENT_LINK_STATUS_OPTIONS]}
          />
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            className="payment-filters__range"
          />
          <Select
            value={sortValue}
            onChange={setSortValue}
            className="payment-filters__select"
            options={SORT_OPTIONS}
            aria-label="Sort payments"
          />
          {hasActiveFilters && (
            <Button icon={<ClearOutlined />} onClick={clearFilters}>
              Clear
            </Button>
          )}
        </div>

        <div className="payment-tabs" role="tablist" aria-label="Filter by payment status">
          {statusTabs.map((tab) => {
            const active = paymentStatusFilter === tab.value;

            return (
              <button
                key={tab.value || "all"}
                type="button"
                role="tab"
                aria-selected={active}
                className={`payment-tab${active ? " payment-tab--active" : ""}`}
                onClick={() => setPaymentStatusFilter(tab.value)}
              >
                {tab.label}
                {tab.count !== null && <span className="payment-tab__count">{tab.count}</span>}
              </button>
            );
          })}
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={payments}
          loading={loading}
          // The sum of the column widths above. Narrower than the viewport the
          // columns simply share the slack; narrower than this and the table
          // scrolls rather than squeezing a name back onto two lines.
          scroll={{ x: 1200 }}
          locale={{ emptyText: emptyState }}
          className="payment-table"
          pagination={{
            current: page + 1,
            pageSize: rowsPerPage,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: [5, 10, 25, 50],
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage - 1);
              setRowsPerPage(nextPageSize);
            },
          }}
        />
      </Card>

      <PaymentDetailsDrawer
        open={detailOpen}
        payment={detailPayment}
        onClose={() => {
          setDetailOpen(false);
          setDetailPayment(null);
        }}
      />

      <Modal
        open={Boolean(confirmation)}
        title={activeConfirmation?.title}
        okText={activeConfirmation?.okText}
        okButtonProps={{
          danger: activeConfirmation?.danger,
          loading: Boolean(actionInFlight),
        }}
        cancelButtonProps={{ disabled: Boolean(actionInFlight) }}
        onCancel={() => {
          if (!actionInFlight) setConfirmation(null);
        }}
        onOk={() => runAction(confirmation.kind, confirmation.payment)}
      >
        <Space orientation="vertical" size={8} style={{ width: "100%" }}>
          <Typography.Text>{activeConfirmation?.body}</Typography.Text>
          {confirmation?.payment && (
            <Typography.Text type="secondary">
              Reference: {confirmation.payment.reference_id}
            </Typography.Text>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default PaymentHistory;
